/**
 * Client-side search in lists (built-in templates and blocks, panels):
 * case-insensitive, umlauts and accents folded ("Grüße" finds "grusse"),
 * every word of the query has to appear somewhere.
 */

export function foldSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
}

/** Also finds "grusse" for "grüße": umlauts count with and without "e". */
function variants(word: string): string[] {
  const folded = foldSearchText(word);
  const plain = word.toLowerCase().replace(/ß/g, "ss").normalize("NFKD").replace(/[̀-ͯ]/g, "");
  return folded === plain ? [folded] : [folded, plain];
}

/**
 * Relevance of `fields` for `query`: 0 = no match, higher = better (a match
 * at the start of the first field counts most).
 */
export function searchScore(query: string, fields: readonly (string | null | undefined)[]): number {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  // Fields with folded umlauts ("ue") and with plain ones ("u"): both spellings find them.
  const haystacks = fields.map((field) => variants(field ?? "").join("\u0000"));
  let score = 0;
  for (const word of words) {
    let best = 0;
    for (const candidate of variants(word)) {
      haystacks.forEach((haystack, index) => {
        const at = haystack.indexOf(candidate);
        if (at < 0) return;
        const before = haystack[at - 1];
        const weight =
          (at === 0 || before === "\u0000" || /\s/.test(before ?? "") ? 3 : 1) / (index + 1);
        best = Math.max(best, weight);
      });
    }
    if (best === 0) return 0;
    score += best;
  }
  return score;
}

/** Items matching the query, best first (stable for equal scores). */
export function searchList<T>(
  items: readonly T[],
  query: string,
  fields: (item: T) => readonly (string | null | undefined)[],
): T[] {
  if (!query.trim()) return [...items];
  return items
    .map((item, index) => ({ item, index, score: searchScore(query, fields(item)) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.item);
}
